import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { getAutoCompleteSearchApi } from '../../apis/admin';

interface Props {
  value: any;
  onChange: (value: any) => void;
  list?: any[];
  serverSide?: boolean;
  label?: string;
  placeholder?: string;
  optionLabel?: string;
  renderOptionFn?: (option: any) => React.ReactNode | string;
  serverConfigs?: {
    url: string;
    payload?: any;
    limit?: number;
  };
  noOptionsText?: string;
  multiple?: boolean;
}

const AutocompleteSearch: React.FC<Props> = ({
  serverSide,
  label = 'Search...',
  placeholder = 'Type here...',
  optionLabel = 'label',
  serverConfigs,
  value,
  onChange,
  list,
  renderOptionFn,
  noOptionsText = 'No option found',
  multiple,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState('');
  // const [open, setOpen] = useState(true);

  const fetchServerSideOptions = useCallback(
    async (search: string) => {
      setLoading(true);
      const filters: any = serverConfigs?.payload?.filters || {};
      filters.search = search;
      const payload: any = {
        page: 1,
        limit: serverConfigs?.limit || 0,
      };
      payload.filters = { ...filters };
      getAutoCompleteSearchApi(serverConfigs?.url || '', payload)
        .then((response: any) => {
          if (response.status) {
            console.log('=======Step 3');
            setOptions(response.data.items);
          }
        })
        .catch((err) => {
          console.log('============autocomplete search result fetching error', err);
        })
        .finally(() => setLoading(false));
    },
    [serverConfigs]
  );

  const filterOptions = useCallback(
    (searchValue: string) => {
      setLoading(true);
      const filteredOptions = (allOptions || []).filter((item: any) =>
        (item[optionLabel] || '').toLowerCase().includes(searchValue.toLowerCase())
      );
      setOptions(filteredOptions);
      setLoading(false);
    },
    [allOptions, optionLabel]
  );

  // ⏱ Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue.length > 0) {
        setDebouncedValue(inputValue);
      }
    }, 500); // delay in ms

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  useEffect(() => {
    if (debouncedValue.length >= 2) {
      if (serverSide) {
        fetchServerSideOptions(debouncedValue);
      } else {
        filterOptions(debouncedValue);
      }
    } else {
      setOptions([]);
    }
  }, [serverSide, debouncedValue, fetchServerSideOptions, filterOptions]);

  useEffect(() => {
    if (!serverSide && Array.isArray(list)) {
      setAllOptions(list);
      setOptions(list);
    }
  }, [serverSide, list]);

  const autocompleteProps: any = {
    multiple,
  };
  if (serverSide) {
    // 🔥 disable client-side filtering
    autocompleteProps.filterOptions = (x: any) => x;
  }

  return (
    <Autocomplete
      fullWidth
      // open={open}
      // onOpen={() => setOpen(true)}
      // onClose={() => setOpen(true)} // 🔥 prevent closing
      // blurOnSelect={false}
      // clearOnBlur={false}
      // clearOnEscape={false}
      // disableClearable={false}
      noOptionsText={noOptionsText}
      multiple={multiple}
      options={options}
      value={value}
      loading={loading}
      onChange={(_, newValue: any) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      getOptionLabel={(row) => {
        return row[optionLabel] ?? '';
      }}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        if (renderOptionFn) {
          return (
            <Box component="li" {...props} key={option.id}>
              {renderOptionFn(option)}
            </Box>
          );
        }
        return (
          <Box component="li" {...props} key={option.id}>
            {option[optionLabel]}
          </Box>
        );
      }}
      {...autocompleteProps}
    />
  );
};

export default AutocompleteSearch;
